export interface AddressInfo {
  type: string
  firstName: string
  lastName: string
  address1: string
  address2: string
  city: string
  county: string
  postCode: string
  country: string
  email?: string
  phone?: string
}

export interface RefundHistoryItem {
  date: string
  amount: string
  reason: string
  method: string
  status: 'completed' | 'failed'
}

export const ORDER_SUBSCRIPTION_TYPES = ['shop_subscription', 'shop_order_refund', 'shop_order'] as const
export type OrderSubscriptionType = (typeof ORDER_SUBSCRIPTION_TYPES)[number]

export interface Order {
  hash: string
  wooOrderId: string | null
  orderNumber: string
  status: string
  userDisplayName: string
  subscriptionType: OrderSubscriptionType
  itemCount: number
  totalAmount: number
  taxAmount: number | null
  billingEmail: string
  userEmail: string | null
  paymentMethod: string | null
  paymentMethodDetail: string | null
  createdVia: string | null
  shippingMethod: string
  deliveryDate: string | null
  fulfillmentType: string | null
  shipping: AddressInfo
  billing: AddressInfo
  items: ProductItem[]
  cancellationReason: string | null
  startedAt?: Date
  updatedAt?: Date
  amountRefunded?: number
  totalAvailableToRefund?: number
  refundHistory?: RefundHistoryItem[]
}

export interface ProductCategory {
  hash: string
  name: string | null
}

export interface ProductItem {
  hash: string
  status: string | null
  name: string | null
  description: string | null
  sku: string | null
  regular_price: string
  sale_price: string | null
  categories: ProductCategory[]
}

export interface OrderListItem {
  id: string
  orderNumber: string | null
  billingEmail: string
  userEmail: string | null
  status: string
  type: OrderSubscriptionType
  totalAmount: number
  paymentMethod: string | null
  createdAt: string
  deliveryDate: string | null
  itemsCount: number
  items: ProductItem[]
  shippingPostcode: string | null
}

/**
 * Reasons a patient can pick when cancelling in Connect. CS gets the same list, but
 * the field is free-text underneath: patients choosing "Something else" write their
 * own explanation, so a stored reason will not always be one of these.
 */
export const CANCELLATION_REASONS = [
  "It's too expensive",
  'Not seeing the results I want',
  'Moving to a different provider',
  'Side effects are not manageable',
  "I've reached my goal weight",
  "Didn't get the support I needed",
  'My treatment has finished',
  'Something else'
] as const

/** `wp_evaro_orders_data.cancellation_reason` is VARCHAR(255); longer values are truncated on write. */
export const CANCELLATION_REASON_MAX_LENGTH = 255

export const REFUND_METHODS = ['Manually', 'Via Credit Cards (Stripe) by Payment Plugins'] as const
export type RefundMethod = (typeof REFUND_METHODS)[number]

export const REFUND_REASONS = [
  'Duplicate order',
  'ID not verified within 14 days',
  'No patient response to query within 14 days',
  'Refunding difference in cost due to change in dose',
  'Refunding difference in cost due to change in quantity',
  'Refunding difference in cost due to change in treatment',
  'Unfortunately we are unable to issue a further course within 12 weeks of last antibiotic course. Please refer to NHS111 if you are unable to contact your dentist',
  'Unfortunately we are unable to issue this treatment for male patients. Please refer to your GP or NHS111',
  'Unfortunately we are unable to proceed with your request as you stated you have not been diagnosed or prescribed this medicine previously. Please refer to your GP or NHS111'
] as const
